'use client';

import { FC, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { CollaboratorDto, PrivilegeEnum } from '@/api/ApiClient';
import { useBackend } from '@/hooks/useBackend';
import { useAuthStore } from '@/stores/authStore';
import DetailField from '@/components/common/DetailField';
import { log } from '@/lib/utils/logger';

interface UserViewDialogProps {
  open: boolean;
  userId: string | null;
  user?: CollaboratorDto | null; // Pre-loaded user data
  onClose: () => void;
}

type UserViewDialogBodyProps = {
  userId: string;
  preloadedUser?: CollaboratorDto | null;
  onClose: () => void;
};

const formatEppnDisplay = (user: CollaboratorDto): string => {
  const rows = user.eppns;
  if (rows?.length) {
    return rows
      .map((row) => {
        const value = row.eppn ?? '';
        if (row.expiresAt) {
          return `${value} (expires ${row.expiresAt})`;
        }
        return value;
      })
      .join(', ');
  }
  return 'N/A';
};

const UserViewDialogBody: FC<UserViewDialogBodyProps> = ({ userId, preloadedUser, onClose }) => {
  const backend = useBackend();
  const { isAuthorized } = useAuthStore();
  const canListUsers = isAuthorized(PrivilegeEnum.User, 'list');

  const [user, setUser] = useState<CollaboratorDto | null>(() => preloadedUser ?? null);
  const [loading, setLoading] = useState(() => !preloadedUser);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preloadedUser) {
      return;
    }

    let cancelled = false;

    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const foundUser = await backend.user.get(userId);

        if (!cancelled) {
          setUser(foundUser);
        }
      } catch (err) {
        log.error('[UserViewDialog] Error fetching user:', err);
        if (!cancelled) {
          setError('Failed to load user details');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchUser();

    return () => {
      cancelled = true;
    };
  }, [backend, preloadedUser, userId]);

  const roleName = user?.roles && user.roles.length > 0 ? user.roles.map((r) => r.name).join(', ') : 'N/A';
  const showSignIn = canListUsers && Boolean(user?.eppns?.length || user?.teamsUserId);
  const showContact = Boolean(
    user?.email || user?.institution || user?.phone || user?.position || user?.city || user?.state || user?.zip
  );

  return (
    <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 2,
        }}
      >
        <Typography variant="h6" component="div">
          User Details
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !user ? (
          <Typography color="error">User not found</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              '& > *': {
                minWidth: 0,
              },
            }}
          >
            {showSignIn ? (
              <>
                <DetailField label="EPPN" value={formatEppnDisplay(user)} isReadOnly={true} width="100%" />
                <DetailField
                  label="Microsoft Teams user ID"
                  value={user.teamsUserId || 'N/A'}
                  isReadOnly={true}
                  width="100%"
                />
              </>
            ) : null}
            <DetailField
              label="Name"
              value={`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}
              isReadOnly={true}
              width="100%"
            />
            <DetailField label="ORCID" value={user.orcId || 'N/A'} isReadOnly={true} width="100%" />
            <DetailField label="Role" value={roleName} isReadOnly={true} width="100%" />
            {showContact ? (
              <>
                <DetailField label="Position" value={user.position || 'N/A'} isReadOnly={true} width="100%" />
                <DetailField label="Institution" value={user.institution || 'N/A'} isReadOnly={true} width="100%" />
                <DetailField label="State" value={user.state || 'N/A'} isReadOnly={true} width="100%" />
                <DetailField label="Zip" value={user.zip || 'N/A'} isReadOnly={true} width="100%" />
                <DetailField label="Country" value={user.country || 'N/A'} isReadOnly={true} width="100%" />
                <DetailField label="Phone" value={user.phone || 'N/A'} isReadOnly={true} width="100%" />
                <DetailField label="Email" value={user.email || 'N/A'} isReadOnly={true} width="100%" />
              </>
            ) : null}
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: 3,
          py: 2,
        }}
      >
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Paper>
  );
};

export const UserViewDialog: FC<UserViewDialogProps> = ({ open, userId, user: preloadedUser, onClose }) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {open && userId ? (
        <UserViewDialogBody key={userId} userId={userId} preloadedUser={preloadedUser} onClose={handleClose} />
      ) : null}
    </Dialog>
  );
};

export default UserViewDialog;
